import React from 'react';
import type { DashboardOverviewData } from '../../../types';
import MetricStrip from './MetricStrip';
import TabSection from './TabSection';
import ChartCard from './charts/ChartCard';
import AreaStackChart from './charts/AreaStackChart';
import CompositionBarChart from './charts/CompositionBarChart';
import DailyFlowChart from './charts/DailyFlowChart';
import StackedBarChart from './charts/StackedBarChart';
import TrendLineChart from './charts/TrendLineChart';
import { BLUE_RAMP, CHART_COLORS } from './charts/common';
import { isCompositionEmpty, isSeriesEmpty, toBucketSeries } from './executiveModel';

interface FinancialsTabProps {
  data: DashboardOverviewData | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

const FEE_SERIES = [
  { dataKey: 'playerDeposits', name: 'Player deposits', color: BLUE_RAMP[1] },
  { dataKey: 'withdrawals', name: 'Withdrawals', color: CHART_COLORS.out },
  { dataKey: 'creditPurchases', name: 'Credit purchases', color: BLUE_RAMP[4] },
];

const MONEY_IN_SERIES = [
  { dataKey: 'platformPlayers', name: 'Platform players', color: BLUE_RAMP[0] },
  { dataKey: 'shopPlayers', name: 'Shop players', color: BLUE_RAMP[2] },
  { dataKey: 'network', name: 'Agent network', color: BLUE_RAMP[4] },
];

const LIABILITY_COLORS = [BLUE_RAMP[1], BLUE_RAMP[3], CHART_COLORS.risk];

/**
 * Platform financials — the full ledger read: P&L and its components, cash
 * flow, credits issued, and the live liability position.
 *
 * This was the Overview tab until the Overview slot was given to the executive
 * summary. Every metric and the `/admin/dashboard/overview` endpoint behind it
 * are unchanged; they are now presented as compact strips so the charts are on
 * screen without scrolling.
 */
const FinancialsTab: React.FC<FinancialsTabProps> = ({ data, isLoading, error, onRetry }) => {
  const flow = toBucketSeries(data?.charts?.flow, ['usdIn', 'payoutsOut', 'net']);
  const fees = toBucketSeries(data?.charts?.fees, [
    'playerDeposits',
    'withdrawals',
    'creditPurchases',
  ]);
  const moneyIn = toBucketSeries(data?.charts?.moneyIn, [
    'platformPlayers',
    'shopPlayers',
    'network',
  ]);
  const credits = toBucketSeries(data?.charts?.credits, ['credits']);

  const bucketNote = data?.charts?.granularity === 'hour' ? 'Hourly buckets' : 'Daily buckets';

  const liabilityMix = data
    ? [
        { name: 'Player credits', value: Math.max(0, data.kpis.playerCreditLiability.value) },
        { name: 'Pending withdrawals', value: Math.max(0, data.kpis.pendingWithdrawalsAmount.value) },
        { name: 'Online debt owed to tiers', value: Math.max(0, data.kpis.onlineDebtOutstanding.value) },
      ]
    : [];

  const exposure = data
    ? data.kpis.playerCreditLiability.value +
      data.kpis.pendingWithdrawalsAmount.value +
      data.kpis.onlineDebtOutstanding.value
    : 0;

  return (
    <TabSection isLoading={isLoading} error={error} onRetry={onRetry}>
      {data && (
        <div className="space-y-4">
          <MetricStrip
            title="Performance"
            columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-8"
            items={[
              { metric: 'platformNetPnl', kpi: data.kpis.platformNetPnl, format: 'currency', emphasis: true },
              { metric: 'feesEarned', kpi: data.kpis.feesEarned, format: 'currency' },
              { metric: 'hierarchyCashCollected', kpi: data.kpis.hierarchyCashCollected, format: 'currency' },
              { metric: 'wholesaleOnlineUsd', kpi: data.kpis.wholesaleOnlineUsd, format: 'currency' },
              { metric: 'usdIn', kpi: data.kpis.usdIn, format: 'currency' },
              { metric: 'payoutsOut', kpi: data.kpis.payoutsOut, format: 'currency', invertDelta: true },
              { metric: 'netCashFlow', kpi: data.kpis.netCashFlow, format: 'currency' },
              { metric: 'creditsIssued', kpi: data.kpis.creditsIssued, format: 'number' },
            ]}
          />

          <MetricStrip
            title="Right now"
            columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-7"
            items={[
              { metric: 'playerCreditLiability', kpi: data.kpis.playerCreditLiability, format: 'currency' },
              { metric: 'pendingWithdrawalsCount', kpi: data.kpis.pendingWithdrawalsCount, format: 'number' },
              { metric: 'pendingWithdrawalsAmount', kpi: data.kpis.pendingWithdrawalsAmount, format: 'currency' },
              { metric: 'onlineDebtOutstanding', kpi: data.kpis.onlineDebtOutstanding, format: 'currency', invertDelta: true },
              { metric: 'shopFundingDebtOutstanding', kpi: data.kpis.shopFundingDebtOutstanding, format: 'currency', invertDelta: true },
              { metric: 'tierUsdFloatTotal', kpi: data.kpis.tierUsdFloatTotal, format: 'currency' },
              { metric: 'exposure', kpi: { value: exposure }, format: 'currency' },
            ]}
          />

          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard
              metric="cashFlowTrend"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(flow, ['usdIn', 'payoutsOut'])}
            >
              <DailyFlowChart data={flow} xKey="label" labelFormatter={(v) => v} />
            </ChartCard>

            <ChartCard
              metric="feesBySource"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(fees, ['playerDeposits', 'withdrawals', 'creditPurchases'])}
            >
              <AreaStackChart data={fees} series={FEE_SERIES} />
            </ChartCard>

            <ChartCard
              metric="moneyInBySource"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(moneyIn, ['platformPlayers', 'shopPlayers', 'network'])}
            >
              <StackedBarChart
                data={moneyIn}
                series={MONEY_IN_SERIES}
                xKey="label"
                labelFormatter={(v) => v}
              />
            </ChartCard>

            <ChartCard
              metric="creditsIssuedTrend"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(credits, ['credits'])}
            >
              <TrendLineChart
                data={credits}
                dataKey="credits"
                name="Credits issued"
                xKey="label"
                labelFormatter={(v) => v}
                color={CHART_COLORS.credits}
              />
            </ChartCard>
          </div>

          <ChartCard metric="liabilityMix" isEmpty={isCompositionEmpty(liabilityMix)}>
            <CompositionBarChart
              data={liabilityMix}
              totalLabel="Total exposure"
              colors={LIABILITY_COLORS}
              footer={
                <span className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">
                    Shop funding debt owed to the platform
                  </span>
                  <span className="tabular-nums font-medium text-blue-950">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(data.kpis.shopFundingDebtOutstanding.value)}
                  </span>
                </span>
              }
            />
          </ChartCard>
        </div>
      )}
    </TabSection>
  );
};

export default FinancialsTab;
